/**
 * Menu desplegable propio del design system (reemplazo del Menu de MUI).
 *
 * - Renderiza via createPortal en document.body
 * - Posicionamiento fixed anclado al trigger con flip vertical
 * - Cierra con Escape, click fuera y Tab; retorna el foco al trigger
 * - Navegacion con flechas entre items (role="menu" / role="menuitem")
 * - z-index: usa el token z-modal para quedar sobre header y sidebar
 */
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';

type DropdownMenuPlacement = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

type DropdownMenuProps = {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  placement?: DropdownMenuPlacement;
  minWidth?: number;
  className?: string;
  /** Id del trigger para aria-labelledby. */
  labelledBy?: string;
  /** Id propio para aria-controls del trigger. Por defecto se autogenera. */
  id?: string;
  children: ReactNode;
};

const GAP = 4;

export function DropdownMenu({
  open,
  onClose,
  anchorRef,
  placement = 'bottom-right',
  minWidth = 200,
  className,
  labelledBy,
  id,
  children,
}: DropdownMenuProps) {
  const autoId = useId();
  const menuId = id ?? autoId;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<{ top: number; left: number } | null>(null);

  const reposition = () => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const toBottom = placement.startsWith('bottom');
    const fitsBelow = a.bottom + GAP + p.height <= vh - GAP;
    const fitsAbove = a.top - GAP - p.height >= GAP;
    const showBelow = toBottom ? fitsBelow || !fitsAbove : !fitsAbove && fitsBelow;

    const top = showBelow ? a.bottom + GAP : a.top - p.height - GAP;
    const alignRight = placement.endsWith('right');
    let left = alignRight ? a.right - p.width : a.left;
    left = Math.max(GAP, Math.min(left, vw - p.width - GAP));
    setStyle({ top: Math.max(GAP, top), left });
  };

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }
    reposition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => reposition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open ]);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const anchor = anchorRef.current;

    const focusables = () =>
      Array.from(
        panel?.querySelectorAll<HTMLElement>(
          '[role="menuitem"]:not([disabled]):not([aria-disabled="true"])',
        ) ?? [],
      );

    // Foco inicial en el primer item.
    focusables()[0]?.focus();

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (
        panel &&
        target &&
        !panel.contains(target) &&
        !(anchor && anchor.contains(target))
      ) {
        onClose();
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        anchor?.focus();
        return;
      }
      if (e.key === 'Tab') {
        onClose();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
        const items = focusables();
        if (items.length === 0) return;
        e.preventDefault();
        const current = items.indexOf(document.activeElement as HTMLElement);
        let next = 0;
        if (e.key === 'ArrowDown') next = (current + 1) % items.length;
        else if (e.key === 'ArrowUp') next = (current - 1 + items.length) % items.length;
        else if (e.key === 'End') next = items.length - 1;
        items[next]?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      id={menuId}
      role="menu"
      aria-orientation="vertical"
      aria-labelledby={labelledBy}
      style={
        style
          ? { top: style.top, left: style.left, visibility: 'visible' as const, minWidth }
          : { top: 0, left: 0, visibility: 'hidden' as const, minWidth }
      }
      className={cn(
        'fixed z-modal rounded-xl border border-slate-100 bg-white py-1 shadow-lg animate-fade-in focus:outline-none',
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}

type DropdownMenuItemProps = {
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
};

export function DropdownMenuItem({ icon, onClick, disabled = false, children, className }: DropdownMenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      className={cn(
        'flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100 focus:bg-slate-100 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        className,
      )}
    >
      {icon && (
        <span className="material-symbols-outlined text-[18px] text-slate-500" aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </button>
  );
}

export function DropdownMenuDivider({ className }: { className?: string }) {
  return <div role="separator" className={cn('my-1 h-px bg-slate-100', className)} />;
}
